import { useEffect, useRef, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { Download, RefreshCw, WifiOff, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Registers the PWA service worker and handles three surface states:
 * – new build available → "Recargar" banner
 * – browser fires beforeinstallprompt → "Instalar" banner
 * – SW registration failure → error notice
 * No-op when served over file://.
 */
export function PwaUpdater() {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [swError, setSwError] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const updateRef = useRef<((reload?: boolean) => Promise<void>) | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || location.protocol === 'file:') return;
    updateRef.current = registerSW({
      immediate: true,
      onNeedRefresh: () => setNeedRefresh(true),
      onRegisterError: () => setSwError(true),
    });
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  async function handleInstall() {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') setInstallPrompt(null);
  }

  return (
    <>
      {needRefresh && (
        <div className="fixed bottom-6 left-1/2 z-[80] flex -translate-x-1/2 items-center gap-3 rounded-xl border border-line bg-panel px-5 py-3 text-sm shadow-pop">
          <span className="font-semibold text-ink">Nueva versión disponible</span>
          <button
            onClick={() => void updateRef.current?.(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary-dark"
          >
            <RefreshCw className="h-4 w-4" strokeWidth={2} />
            Recargar
          </button>
        </div>
      )}
      {!needRefresh && installPrompt && (
        <div className="fixed bottom-6 right-4 z-[80] flex max-w-[calc(100vw-2rem)] items-center gap-3 rounded-xl border border-line bg-panel px-4 py-3 text-sm shadow-pop">
          <Download className="h-4 w-4 shrink-0 text-primary-dark" strokeWidth={2} />
          <span className="font-semibold text-ink">Instalar Cumpli</span>
          <button
            onClick={() => void handleInstall()}
            className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary-dark"
          >
            Instalar
          </button>
          <button onClick={() => setInstallPrompt(null)} aria-label="Cerrar" className="text-ink-faint hover:text-ink-soft">
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      )}
      {swError && !needRefresh && (
        <div className="fixed bottom-6 left-1/2 z-[80] flex -translate-x-1/2 items-center gap-3 rounded-xl border border-tone-red-bg bg-panel px-5 py-3 text-sm shadow-pop">
          <WifiOff className="h-4 w-4 shrink-0 text-tone-red" strokeWidth={2} />
          <span className="text-ink">Modo sin conexión no disponible</span>
          <button onClick={() => setSwError(false)} aria-label="Cerrar" className="text-ink-faint hover:text-ink-soft">
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
      )}
    </>
  );
}
